import React, { useEffect, useRef, useState } from "react";
import ReactEcharts from "echarts-for-react";
import { barChart } from "ionicons/icons";
import {
  IonButton,
  IonCard,
  IonCardHeader,
  IonIcon,
  IonSpinner
} from "@ionic/react";
import numbro from "numbro";
import moment from "moment";

const MILLISECONDS_IN_A_DAY = 24 * 60 * 60 * 1000;
const MILLISECONDS_IN_A_WEEK = 7 * MILLISECONDS_IN_A_DAY;
const MILLISECONDS_IN_A_MONTH = 30 * MILLISECONDS_IN_A_DAY;

interface Props {
  data: [string, number][];
  disableFilter?: boolean;
  title?: string;
  considerTimezone?: boolean;
}

const GradientLineChart: React.FC<Props> = ({
  data,
  disableFilter,
  title,
  considerTimezone
}) => {
  const chartRef: any = useRef(null);
  const filters = [
    { name: "24H", value: MILLISECONDS_IN_A_DAY },
    { name: "1W", value: MILLISECONDS_IN_A_WEEK },
    { name: "1M", value: MILLISECONDS_IN_A_MONTH },
    { name: "ALL", value: Infinity }
  ];
  const [filter, setFilter] = useState(filters[0].value);
  const [isDelayOver, setIsDelayOver] = useState(false);

  useEffect(() => {
    if (!isDelayOver || !chartRef.current) {
      // If isDelayOver is false or chartRef.current is not available, don't proceed.
      return;
    }

    const chartInstance = chartRef.current.getEchartsInstance();
    if (chartInstance) {
      const chartElement = chartInstance.getDom();
      chartElement.style.margin = "0";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDelayOver, chartRef.current]);

  useEffect(() => {
    if (data && !isDelayOver) {
      setTimeout(() => {
        setIsDelayOver(true);
      }, 1000);
    }
  }, [data, isDelayOver]);

  if (!data) return null;

  const now =
    data?.length > 0
      ? new Date(data[data.length - 1][0]).getTime()
      : Date.now();
  const filteredData = data.filter(
    ([time, _]) => now - new Date(time).getTime() <= filter
  );

  let sampledData = filteredData;
  if (filteredData.length > 24) {
    let sampleRate = Math.ceil(filteredData.length / 24);
    sampleRate = Math.max(1, sampleRate);
    sampledData = filteredData.filter((_, index) => index % sampleRate === 0);
  }
  sampledData.push([data[data.length - 1][0], data[data.length - 1][1]]);

  let baseValue = 0,
    highestValue = 0,
    lowestValue = 0;
  if (sampledData.length > 0) {
    baseValue = Number(sampledData[0][1]);
    highestValue = Math.max(...sampledData.map(([_time, value]) => value));
    lowestValue = Math.min(...sampledData.map(([_time, value]) => value));
  }

  const stepSize =
    highestValue !== lowestValue
      ? Math.ceil((highestValue - lowestValue) / 5)
      : 1;

  const formatShortNumber = (num: number): string => {
    if (num >= 1000 && num < 1000000) {
      return `${numbro(num / 1000).format("0a")}k`;
    } else {
      return numbro(num).format("0a");
    }
  };

  if (sampledData.length === 0) return null;

  const option = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(145,143,255,0.8)",
      textStyle: {
        color: "#FFFFFF"
      },
      formatter: function (params: any) {
        const date = new Date(params[0].value[0]);
        const formattedDate = moment(date)
          .add(considerTimezone ? 2 : 0, "h")
          .format("DD/MM/YYYY HH:mm");
        const value = parseFloat(params[0].value[1]).toFixed(2);
        return `${formattedDate}</br>${value} USDT`;
      }
    },
    animation: true, // Enable animation
    animationDuration: 1000, // Animation duration in milliseconds
    animationEasing: "cubicOut", // Animation easing function
    xAxis: {
      type: "time",
      axisLine: {
        onZero: false
      },

      boundaryGap: false,
      splitLine: {
        show: true,
        lineStyle: {
          type: "solid",
          color: "rgba(211, 211, 211, 0.1)"
        }
      },
      axisLabel: {
        textStyle: {
          color: "#FFFFFF"
        },
        formatter: function (value: number) {
          const date = new Date(value);
          const day = date.toLocaleString("en-us", { weekday: "short" });
          const dayOfMonth = date.getDate();
          if (filter === MILLISECONDS_IN_A_DAY) {
            const hour = date.getHours().toString().padStart(2, "0");
            const minute = date.getMinutes().toString().padStart(2, "0");
            return `{a|${day}}\n${dayOfMonth}\n${hour}:${minute}`;
          }
          return `{a|${day}}\n${dayOfMonth}`;
        },
        rich: {
          a: {
            fontWeight: "bold"
          }
        }
      }
    },
    yAxis: {
      type: "value",
      max: highestValue + stepSize,
      min: lowestValue - stepSize,

      axisLabel: {
        textStyle: {
          color: "#FFFFFF"
        },
        formatter: function (value: any) {
          return value >= baseValue
            ? "+" + formatShortNumber(value)
            : formatShortNumber(value);
        }
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "solid",
          color: "rgba(211, 211, 211, 0.1)"
        }
      }
    },
    series: [
      {
        data: sampledData?.map(([time, value]) => ({
          name: time,
          value: [time, value],
          itemStyle: {
            // @ts-ignore
            color: value >= baseValue ? "#00ff00" : "#ff0000"
          }
        })),
        type: "line",
        encode: {
          x: 0,
          y: 1
        },
        smooth: true,
        lineStyle: {
          color: `${
            sampledData[sampledData.length - 1][1] >= baseValue
              ? "#00ff00"
              : "#ff2600"
          }`
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: `${
                  sampledData[sampledData.length - 1][1] >= baseValue
                    ? "#00ff00"
                    : "#ff2600"
                }`
              },
              {
                offset: 1,
                color: "transparent" // color at 100% position (transparent)
              }
            ],
            global: false // false by default
          }
        }
      }
    ]
  };
  return (
    <IonCard className="relative bg-primary-80 text-white">
      {!isDelayOver ? (
        <div className="flex justify-center items-center h-[350px]">
          <IonSpinner name="crescent" />
        </div>
      ) : (
        <>
          <IonCardHeader className="absolute top-0 right-0 z-10 flex p-4">
            {!disableFilter &&
              filters.map(f => (
                <IonButton
                  key={f.name}
                  size="small"
                  className={`ml-2 rounded-lg shadow-lg ${
                    filter !== f.value ? "bg-primary-90" : "bg-primary-50"
                  }`}
                  fill="clear"
                  onClick={() => setFilter(f.value)}
                >
                  {f.name}
                </IonButton>
              ))}
          </IonCardHeader>
          <div className="pt-6 pb-4 px-4">
            <div className="flex items-center mb-4">
              <IonIcon className="mr-2 text-primary text-2xl" icon={barChart} />
              <div className="text-xl font-semibold">{title || "Balance"}</div>
            </div>
            <div className="-mt-10 w-full">
              {sampledData.length > 0 && isDelayOver && (
                <ReactEcharts
                  ref={chartRef}
                  option={option}
                  style={{ height: "320px" }}
                />
              )}
            </div>
          </div>
        </>
      )}
    </IonCard>
  );
};

export default GradientLineChart;
